import { useCallback, useEffect, useRef, useState } from 'react'
import { Bot, X, Send, Minimize2, Loader2, RotateCcw, MessageSquarePlus } from 'lucide-react'
import { fetchSetting } from '@/services/settings.service'
import { fetchZoneQuotazioni, fetchCoefficientiExtra } from '@/services/zone-quotazioni.service'
import { saveChatLead } from '@/services/lead.service'
import { saveConversation } from '@/services/chat.service'
import type { ZoneQuotazione } from '@/data/zone-quotazioni'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

const STORAGE_KEY = 'immo_ai_chat'
const TOOLTIP_DISMISSED_KEY = 'immo_ai_tooltip_dismissed'

const INITIAL_SUGGESTIONS = [
  'Valuta il mio immobile',
  'Quanto vale un bilocale in centro?',
  'Quali servizi offrite?',
  'Prenota una consulenza',
]

const POST_VALUATION_SUGGESTIONS = [
  'Vorrei essere ricontattato',
  'Come vendere al miglior prezzo',
  'Chiama l\'agenzia',
]

function generateSessionId(): string {
  return 'chat_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9)
}

function formatZoneDataForPrompt(zones: ZoneQuotazione[]): string {
  return zones
    .map(
      (z) =>
        `- ${z.nome}: Abitabile €${z.prezzoAbitabile}/mq, Ristrutturato €${z.prezzoRistrutturato}/mq, Da ristrutturare €${z.prezzoDaRistrutturare}/mq | Vista mare +${(z.vistaMareBonus * 100).toFixed(0)}%, Pertinenza +${(z.pertinenzaBonus * 100).toFixed(0)}%, Esposizione +${(z.esposizioneBonus * 100).toFixed(0)}%, Spazi ext. +${(z.spaziEsterniBonus * 100).toFixed(0)}%, No ascensore ${(z.senzaAscensore * 100).toFixed(0)}% | Casa x${z.casa}, Villa x${z.villa}`
    )
    .join('\n')
}

// Lightweight markdown renderer for assistant messages
function renderMarkdown(text: string): JSX.Element[] {
  const lines = text.split('\n')
  const elements: JSX.Element[] = []
  let listItems: string[] = []

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} className="list-disc list-inside my-1 space-y-0.5">
          {listItems.map((item, i) => (
            <li key={i}>{formatInline(item)}</li>
          ))}
        </ul>
      )
      listItems = []
    }
  }

  const formatInline = (str: string): JSX.Element => {
    const parts: (string | JSX.Element)[] = []
    let remaining = str
    let keyIdx = 0

    // Process bold, links, and inline code
    while (remaining.length > 0) {
      // Bold: **text**
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/)
      // Link: [text](url)
      const linkMatch = remaining.match(/\[(.+?)\]\((https?:\/\/.+?)\)/)

      const matches = [
        boldMatch ? { type: 'bold', index: boldMatch.index!, match: boldMatch } : null,
        linkMatch ? { type: 'link', index: linkMatch.index!, match: linkMatch } : null,
      ].filter(Boolean).sort((a, b) => a!.index - b!.index)

      if (matches.length === 0) {
        parts.push(remaining)
        break
      }

      const first = matches[0]!
      if (first.index > 0) {
        parts.push(remaining.slice(0, first.index))
      }

      if (first.type === 'bold') {
        parts.push(<strong key={`b-${keyIdx++}`}>{first.match[1]}</strong>)
        remaining = remaining.slice(first.index + first.match[0].length)
      } else if (first.type === 'link') {
        parts.push(
          <a key={`a-${keyIdx++}`} href={first.match[2]} target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-700">
            {first.match[1]}
          </a>
        )
        remaining = remaining.slice(first.index + first.match[0].length)
      }
    }

    return <span>{parts}</span>
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // List item
    if (/^[-•]\s+/.test(line)) {
      listItems.push(line.replace(/^[-•]\s+/, ''))
      continue
    }

    // Numbered list
    if (/^\d+\.\s+/.test(line)) {
      listItems.push(line.replace(/^\d+\.\s+/, ''))
      continue
    }

    flushList()

    // Empty line
    if (line.trim() === '') {
      elements.push(<br key={`br-${i}`} />)
      continue
    }

    // Regular text
    elements.push(
      <p key={`p-${i}`} className="my-0.5">
        {formatInline(line)}
      </p>
    )
  }

  flushList()
  return elements
}

// Extract and remove LEAD_DATA tag from message
function extractLeadData(text: string): { cleanText: string; leadData: Record<string, string> | null } {
  const match = text.match(/\[LEAD_DATA:\s*(\{[^}]+\})\s*\]/)
  if (!match) return { cleanText: text, leadData: null }

  try {
    const leadData = JSON.parse(match[1])
    const cleanText = text.replace(match[0], '').trim()
    return { cleanText, leadData }
  } catch {
    return { cleanText: text, leadData: null }
  }
}

export function AiAssistant() {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [zoneDataStr, setZoneDataStr] = useState('')
  const [coefficientiStr, setCoefficientiStr] = useState('')
  const [dataLoaded, setDataLoaded] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [lastFailedMsg, setLastFailedMsg] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<string[]>(INITIAL_SUGGESTIONS)
  const [showTooltip, setShowTooltip] = useState(false)
  const [sessionId] = useState(generateSessionId)
  const [hasValuation, setHasValuation] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Check if AI assistant is enabled
  useEffect(() => {
    fetchSetting('ai_assistant_enabled').then((value) => {
      setEnabled(value === 'true')
    })
  }, [])

  // Restore messages from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved) as Message[]
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed)
        }
      }
    } catch {
      // ignore
    }
  }, [])

  // Save messages to sessionStorage on change
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
    }
  }, [messages])

  // Proactive tooltip after 20 seconds
  useEffect(() => {
    if (enabled !== true) return
    const dismissed = sessionStorage.getItem(TOOLTIP_DISMISSED_KEY)
    if (dismissed) return

    tooltipTimerRef.current = setTimeout(() => {
      setShowTooltip(true)
    }, 20000)

    return () => {
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current)
    }
  }, [enabled])

  // Load zone data when chat opens for the first time
  const loadData = useCallback(async () => {
    if (dataLoaded) return
    try {
      const [zones, coefficienti] = await Promise.all([
        fetchZoneQuotazioni(),
        fetchCoefficientiExtra(),
      ])
      setZoneDataStr(formatZoneDataForPrompt(zones))
      setCoefficientiStr(
        `Box: +${(coefficienti.box * 100).toFixed(1)}%, Posto auto: +${(coefficienti.postoAuto * 100).toFixed(1)}%, Piscina: +${(coefficienti.piscina * 100).toFixed(0)}%, Residence: +${(coefficienti.residence * 100).toFixed(0)}%`
      )
      setDataLoaded(true)
    } catch (err) {
      console.error('Errore caricamento dati zone:', err)
    }
  }, [dataLoaded])

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Detect if valuation was given (contains euro range pattern)
  const checkForValuation = (text: string) => {
    if (/€\s*[\d.]+/.test(text) && /range|stima|valore|valutazione/i.test(text)) {
      setHasValuation(true)
      setSuggestions(POST_VALUATION_SUGGESTIONS)
    }
  }

  // Process completed assistant message: extract lead data, save conversation
  const processCompletedMessage = async (allMessages: Message[], rawContent: string) => {
    const { cleanText, leadData } = extractLeadData(rawContent)

    // Update message with cleaned text (without lead tag)
    if (leadData) {
      const updated = allMessages.map((m, i) =>
        i === allMessages.length - 1 ? { ...m, content: cleanText } : m
      )
      setMessages(updated)

      try {
        await saveChatLead({
          name: leadData.name || '',
          email: leadData.email || '',
          phone: leadData.phone || '',
          zone: leadData.zone || '',
          stima: leadData.stima || '',
          sessionId,
        })
      } catch (err) {
        console.error('Errore salvataggio lead da chat:', err)
      }
    }

    checkForValuation(cleanText || rawContent)

    // Save conversation to Supabase
    const finalMessages = leadData
      ? allMessages.map((m, i) =>
          i === allMessages.length - 1 ? { ...m, content: cleanText } : m
        )
      : allMessages
    try {
      await saveConversation({
        sessionId,
        messages: finalMessages,
        valuationCompleted: hasValuation || /€\s*[\d.]+/.test(rawContent),
      })
    } catch (err) {
      console.error('Errore salvataggio conversazione:', err)
    }
  }

  const handleOpen = async () => {
    setIsOpen(true)
    setShowTooltip(false)
    sessionStorage.setItem(TOOLTIP_DISMISSED_KEY, 'true')
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current)

    await loadData()
    // Send initial greeting if no messages yet
    if (messages.length === 0) {
      sendGreeting()
    }
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  const dismissTooltip = () => {
    setShowTooltip(false)
    sessionStorage.setItem(TOOLTIP_DISMISSED_KEY, 'true')
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current)
  }

  const sendGreeting = async () => {
    setIsStreaming(true)

    try {
      let zData = zoneDataStr
      let cData = coefficientiStr
      if (!zData) {
        const [zones, coefficienti] = await Promise.all([
          fetchZoneQuotazioni(),
          fetchCoefficientiExtra(),
        ])
        zData = formatZoneDataForPrompt(zones)
        cData = `Box: +${(coefficienti.box * 100).toFixed(1)}%, Posto auto: +${(coefficienti.postoAuto * 100).toFixed(1)}%, Piscina: +${(coefficienti.piscina * 100).toFixed(0)}%, Residence: +${(coefficienti.residence * 100).toFixed(0)}%`
        setZoneDataStr(zData)
        setCoefficientiStr(cData)
        setDataLoaded(true)
      }

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: 'Ciao, vorrei una valutazione del mio immobile a Sanremo.' }],
          zoneData: zData,
          coefficienti: cData,
        }),
      })

      if (!response.ok) throw new Error('Errore API')

      const assistantMsg: Message = { role: 'assistant', content: '' }
      setMessages([assistantMsg])

      let finalContent = ''
      await readStream(response, (text) => {
        finalContent = text
        assistantMsg.content = text
        setMessages([{ ...assistantMsg }])
      })

      await processCompletedMessage([{ ...assistantMsg, content: finalContent }], finalContent)
    } catch (err) {
      console.error('Errore greeting:', err)
      setMessages([
        {
          role: 'assistant',
          content:
            'Ciao! Sono l\'assistente di ImmoSanremo. Posso aiutarti a ottenere una valutazione del tuo immobile a Sanremo. In quale zona si trova?',
        },
      ])
    } finally {
      setIsStreaming(false)
    }
  }

  const readStream = async (
    response: Response,
    onUpdate: (fullText: string) => void
  ) => {
    const reader = response.body?.getReader()
    if (!reader) return

    const decoder = new TextDecoder()
    let fullText = ''
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      // Keep last element as buffer (may be incomplete)
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6)
          if (data === '[DONE]') continue

          try {
            const parsed = JSON.parse(data)
            const delta = parsed.choices?.[0]?.delta?.content
            if (delta) {
              fullText += delta
              onUpdate(fullText)
            }
          } catch {
            // Skip unparseable lines
          }
        }
      }
    }

    // Process any remaining buffer
    if (buffer.startsWith('data: ')) {
      const data = buffer.slice(6)
      if (data !== '[DONE]') {
        try {
          const parsed = JSON.parse(data)
          const delta = parsed.choices?.[0]?.delta?.content
          if (delta) {
            fullText += delta
            onUpdate(fullText)
          }
        } catch {
          // ignore
        }
      }
    }
  }

  const handleSend = async (overrideText?: string) => {
    const trimmed = (overrideText || input).trim()
    if (!trimmed || isStreaming) return

    const userMsg: Message = { role: 'user', content: trimmed }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setInput('')
    setIsStreaming(true)
    setLastFailedMsg(null)

    const apiMessages = newMessages.map((m) => ({
      role: m.role,
      content: m.content,
    }))

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: apiMessages,
          zoneData: zoneDataStr,
          coefficienti: coefficientiStr,
        }),
      })

      if (!response.ok) throw new Error('Errore API')

      const assistantMsg: Message = { role: 'assistant', content: '' }
      setMessages([...newMessages, assistantMsg])
      setRetryCount(0)

      let finalContent = ''
      await readStream(response, (text) => {
        finalContent = text
        assistantMsg.content = text
        setMessages([...newMessages, { ...assistantMsg }])
      })

      await processCompletedMessage(
        [...newMessages, { ...assistantMsg, content: finalContent }],
        finalContent
      )
    } catch (err) {
      console.error('Errore chat:', err)
      setLastFailedMsg(trimmed)
      const newRetry = retryCount + 1
      setRetryCount(newRetry)

      const errorContent = newRetry >= 2
        ? 'Mi scuso, il servizio non e\' momentaneamente disponibile.\n\nPuoi contattarci direttamente:\n- **Telefono**: +39 0184 610689\n- **Email**: info@immosanremo.com\n- **Orari**: Lun-Ven 9:00-18:00, Sab 9:00-13:00'
        : 'Mi scuso, si e\' verificato un errore. Puoi riprovare oppure contattarci al +39 0184 610689.'

      setMessages([
        ...newMessages,
        { role: 'assistant', content: errorContent },
      ])
    } finally {
      setIsStreaming(false)
    }
  }

  const handleRetry = () => {
    if (lastFailedMsg) {
      // Remove the last error message and resend
      const withoutError = messages.slice(0, -2) // remove user msg + error response
      setMessages(withoutError)
      handleSend(lastFailedMsg)
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    handleSend(suggestion)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleNewConversation = () => {
    setMessages([])
    setHasValuation(false)
    setSuggestions(INITIAL_SUGGESTIONS)
    setRetryCount(0)
    setLastFailedMsg(null)
    sessionStorage.removeItem(STORAGE_KEY)
    sendGreeting()
  }

  // Don't render if not enabled or still loading setting
  if (enabled !== true) return null

  return (
    <>
      {/* Chat bubble + tooltip */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
          {/* Proactive tooltip */}
          {showTooltip && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3 max-w-[260px] animate-slide-up">
              <p className="text-sm text-gray-700 mb-2">
                Vuoi una <strong>stima gratuita</strong> del tuo immobile a Sanremo?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleOpen}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium py-1.5 px-3 rounded-lg transition-colors"
                >
                  Si, aiutami
                </button>
                <button
                  onClick={dismissTooltip}
                  className="text-xs text-gray-400 hover:text-gray-600 py-1.5 px-2"
                >
                  No grazie
                </button>
              </div>
            </div>
          )}
          <button
            onClick={handleOpen}
            className="bg-primary-600 hover:bg-primary-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 animate-bounce-gentle"
            aria-label="Apri assistente AI"
          >
            <Bot className="h-6 w-6" />
          </button>
        </div>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[400px] h-[100dvh] sm:h-[600px] sm:max-h-[80vh] flex flex-col bg-white sm:rounded-2xl shadow-2xl border border-gray-200 animate-slide-up">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white sm:rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-1.5 rounded-lg">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Assistente ImmoSanremo</h3>
                <p className="text-xs text-white/70">Valutazioni immobiliari Sanremo</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleNewConversation}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Nuova conversazione"
                title="Nuova conversazione"
              >
                <MessageSquarePlus className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Minimizza"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-primary-600 text-white rounded-br-md whitespace-pre-wrap'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}
                >
                  {msg.content ? (
                    msg.role === 'assistant' ? (
                      <div className="space-y-0">{renderMarkdown(msg.content)}</div>
                    ) : (
                      msg.content
                    )
                  ) : (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-gray-400">Sto scrivendo...</span>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Retry button */}
            {lastFailedMsg && !isStreaming && retryCount < 2 && (
              <div className="flex justify-center">
                <button
                  onClick={handleRetry}
                  className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-full transition-colors"
                >
                  <RotateCcw className="h-3 w-3" />
                  Riprova
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggestion chips */}
          {suggestions.length > 0 && !isStreaming && !input && (
            <div className="px-3 pb-1 flex gap-2 overflow-x-auto scrollbar-hide">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSuggestionClick(s)}
                  className="shrink-0 text-xs bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-gray-200 p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Scrivi un messaggio..."
                rows={1}
                className="flex-1 resize-none border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent max-h-24"
                disabled={isStreaming}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isStreaming}
                className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white p-2.5 rounded-xl transition-colors shrink-0"
                aria-label="Invia messaggio"
              >
                {isStreaming ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">
              Valutazione indicativa generata da AI — per un report completo usa il{' '}
              <a href="/valutazione" className="text-primary-600 hover:underline">
                form di valutazione
              </a>
            </p>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .animate-bounce-gentle {
          animation: bounce-gentle 2s ease-in-out infinite;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  )
}
